import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import s from './CountPill.module.css'

/**
 * A NUMBER IN A PILL — the one shape seven surfaces had each drawn themselves.
 *
 * `Badge` is not this: it is uppercase 800-weight text for a STATUS word, and a
 * count set in it reads as a label. `Pill` is not this either: it is a tinted
 * chip at `--fs-sm` carrying a value. A count is neither — it is a figure,
 * which is why the one property none of the copies ever forgot is
 * `font-variant-numeric: tabular-nums`. A number that shifts its own width as
 * it counts up is the tell.
 *
 * The copies, before this: `Board.module.css`'s `.count` (already in the kit,
 * but private to that one component), `Lookup`'s `.advN` (likewise),
 * `RecentPinnedPanel`'s `.titleBarCount` and `.sectionCount`,
 * `RelationshipWorkspace`'s `.railCount`, `CommandPalette`'s `.groupCount` and
 * the triage board's `.colCount`.
 *
 * ── tone is presentation, not status ──────────────────────────────────────
 * `brand` marks the count a surface is ABOUT; `outline` sits beside a section
 * heading, where a filled pill would compete with it; `neutral` is a quiet
 * tally. None of the three means good or bad — a count has no health, and
 * giving it `Tone` would invite `critical` on a number that is merely large.
 */
export interface CountPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'brand' | 'outline'
  children: ReactNode
}

export function CountPill({ tone = 'neutral', className, children, ...rest }: CountPillProps) {
  return (
    <span className={cx(s.count, s[tone], className)} {...rest}>{children}</span>
  )
}
